import React, { useEffect, useState, useRef, useCallback } from "react";
import LoadingWheel from "../../components/Shared/LoadingWheel";
import { withAgency } from "../../data/withAgency";
import { BaseCard } from "../../components/NurturingCampaign/BaseCard";
import { StyledSelectInput } from "../../components/Shared/StyledInputs/StyledSelectInput";
import { ButtonInput } from "../../components/Shared/Inputs/ButtonInput";
import { useNurtureCampaigns } from "../../data/nurtureCampaigns/useNurtureCampaigns";
import { useForm } from "react-hook-form";
import { CampaignStatsDisplay } from "../../components/NurturingCampaign/CampaignStatsDisplay";
import { PageHeader } from "../../components/Shared/PageHeader";

interface NurtureCampaignSelectionFormFields {
  campaignId: string;
}

interface NurturingCampaignStatsProps {
  agency: any;
  agencyReady: boolean;
}

function _CampaignStats({ agency, agencyReady }: NurturingCampaignStatsProps) {
  const { nurtureCampaigns } = useNurtureCampaigns(agency.id);
  const { register, handleSubmit, watch, setValue } = useForm<NurtureCampaignSelectionFormFields>();
  const [campaignId, setCampaignId] = useState<string>();
  const initialMount = useRef(true);
  const debounceRef = useRef<number | null>(null);
  const watchedCampaign = watch("campaignId");

  const onSubmit = useCallback((data: NurtureCampaignSelectionFormFields) => {
    setCampaignId(data.campaignId);
  }, []);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      handleSubmit(onSubmit)();
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [watchedCampaign, handleSubmit, onSubmit]);

  useEffect(() => {
    if (nurtureCampaigns.data?.data && nurtureCampaigns.data.data?.length > 0) {
      setValue("campaignId", nurtureCampaigns.data.data[0]?.id);
      setCampaignId(nurtureCampaigns.data.data[0]?.id);
    }
  }, [nurtureCampaigns.data?.data, setValue])

  if (!agencyReady || nurtureCampaigns.isLoading) {
    return <div className="flex py-10 justify-center"><LoadingWheel width="w-10" height="h-10" /></div>
  } else {
    return (
      <div className="px-4 sm:px-6 lg:px-8 space-y-6">
        <PageHeader
          title="Campaign Statistics"
          description="View and analyze your campaign performance"
        />
        <BaseCard title="Choose Campaign">
          <form onSubmit={handleSubmit(onSubmit)} className="flex items-center space-x-4">
            <StyledSelectInput
              classes="w-96"
              options={nurtureCampaigns.data?.data?.map(campaign => ({
                value: campaign.id,
                label: campaign.name
              })) ?? []}
              registerName="campaignId"
              register={register}
              setValue={setValue}
              watch={watch}
            />
          </form>
        </BaseCard>
        <CampaignStatsDisplay agencyId={agency.id} campaignId={campaignId!} />
      </div>
    );
  }
}

const CampaignStats = withAgency(_CampaignStats);

export default CampaignStats;
